import { Box, Typography } from '@mui/material';
import React, { useEffect } from 'react';

import { useInstallationGuide } from '../../../hooks/useInstallationGuide';
import { GuidanceSection } from '../GuidanceSection';
import { WizardNavigation } from '../WizardNavigation';

/** Path to phone image (with WiFi icon) */
const PHONE_IMAGE_PATH = '/assets/images/installation-guide/phone.png';

/** Path to Core device image */
const CORE_IMAGE_PATH = '/assets/images/installation-guide/core-splash.png';

/**
 * ReadyTestStep - Slide 3.1: Ready to Test Connection
 *
 * Shows WiFi verification guidance before testing connection:
 * - Phone with WiFi icon + Core device image side-by-side
 * - Instructions to verify same network
 * - Two chatbot help buttons for WiFi troubleshooting
 * - "Test Connection" button to proceed
 */
export const ReadyTestStep: React.FC = () => {
  const { goToStep, setMainStepId } = useInstallationGuide();

  useEffect(() => {
    setMainStepId('step_readyTestConnection');
  }, [setMainStepId]);

  const handleTestConnection = () => {
    goToStep('connection-test');
  };

  const guidanceButtons = [
    { label: __("I'm not on WiFi"), flowId: 'wifi-help' },
    { label: __("I'm not sure if I'm on the network"), flowId: 'wifi-help' },
  ];

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h3" component="h1" sx={{ mb: 1, textAlign: 'center' }}>
        {__('Ready to test the connection?')}
      </Typography>

      <Typography variant="h6" sx={{ mb: 3, color: 'text.secondary' }}>
        {__("Let's make sure your Core is available on your network.")}
      </Typography>

      {/* Phone + Core image section */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 2, sm: 4 },
          my: 2,
          py: 2,
        }}
      >
        {/* Phone with WiFi icon */}
        <Box
          component="img"
          src={PHONE_IMAGE_PATH}
          alt={__('Phone connected to WiFi')}
          sx={{
            maxWidth: { xs: '35%', sm: '30%' },
            maxHeight: 200,
            objectFit: 'contain',
          }}
        />

        {/* Core device */}
        <Box
          component="img"
          src={CORE_IMAGE_PATH}
          alt={__('Fusus Core device')}
          sx={{
            maxWidth: { xs: '50%', sm: '45%' },
            maxHeight: 200,
            objectFit: 'contain',
          }}
        />
      </Box>

      <Typography
        variant="body1"
        sx={{
          mb: 2,
          fontWeight: 500,
        }}
      >
        {__('Make sure your phone is connected to the same network as the Core.')}
      </Typography>

      {/* Guidance section with chatbot buttons */}
      <GuidanceSection
        buttons={guidanceButtons}
      />

      <WizardNavigation
        primaryLabel={__('Test Connection')}
        onPrimary={handleTestConnection}
      />
    </Box>
  );
};
